import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle,
  Clock,
  MoreVertical,
  Building2,
  Calendar,
  Edit2,
  Trash2,
  MessageSquare,
  Eye,
  User,
  UserPlus,
  UserCheck,
  UserX,
  ArrowUpCircle,
  AlertTriangle,
  Zap,
  Timer,
  CheckCircle2,
  Circle,
  Play,
  Pause,
  Star
} from 'lucide-react';
import { Button } from "../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.jsx";
import { Badge } from "../ui/badge.jsx";
import { ScrollArea } from "../ui/scroll-area.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.jsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "../ui/dropdown-menu.jsx";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip.jsx";
import { useNavigate } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { User as UserEntity } from '../../api/supabaseEntities.js';
import { getTimeElapsedString } from '../../utils/dateHelpers.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../ui/dialog.jsx';
import { Input } from '../ui/input.jsx';

const COLUMNS = {
  open: {
    title: 'Abertos',
    color: 'border-t-red-500 bg-gradient-to-br from-red-50 to-orange-50',
    headerColor: 'bg-gradient-to-r from-red-500 to-orange-500',
    icon: AlertCircle,
    iconColor: 'text-red-600',
    count: 0
  },
  analysis: {
    title: 'Em Análise',
    color: 'border-t-purple-500 bg-gradient-to-br from-purple-50 to-violet-50',
    headerColor: 'bg-gradient-to-r from-purple-500 to-violet-500',
    icon: Timer,
    iconColor: 'text-purple-600',
    count: 0
  },
  in_progress: {
    title: 'Em Andamento',
    color: 'border-t-blue-500 bg-gradient-to-br from-blue-50 to-cyan-50',
    headerColor: 'bg-gradient-to-r from-blue-500 to-cyan-500',
    icon: Play,
    iconColor: 'text-blue-600',
    count: 0
  },
  resolved: {
    title: 'Resolvidos',
    color: 'border-t-green-500 bg-gradient-to-br from-green-50 to-emerald-50',
    headerColor: 'bg-gradient-to-r from-green-500 to-emerald-500',
    icon: CheckCircle2,
    iconColor: 'text-green-600',
    count: 0
  }
};

const TicketCard = ({ ticket, company, client, assignedUser, availableUsers, index, onUpdate, onDelete, onAssign }) => {
  const navigate = useNavigate();
  
  const getPriorityConfig = (priority) => {
    const configs = {
      low: {
        badge: 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border-blue-300 shadow-sm',
        icon: Circle,
        label: 'Baixa',
        iconColor: 'text-blue-600'
      },
      medium: {
        badge: 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border-yellow-300 shadow-sm',
        icon: ArrowUpCircle,
        label: 'Média',
        iconColor: 'text-yellow-600'
      },
      high: {
        badge: 'bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border-orange-300 shadow-sm',
        icon: AlertTriangle,
        label: 'Alta',
        iconColor: 'text-orange-600'
      },
      critical: {
        badge: 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300 shadow-sm',
        icon: Zap,
        label: 'Crítica',
        iconColor: 'text-red-600'
      }
    };
    return configs[priority] || configs.medium;
  };

  const getTypeConfig = (type) => {
    const configs = {
      support: { label: 'Suporte', color: 'bg-blue-50 text-blue-700 border-blue-200' },
      development: { label: 'Desenvolvimento', color: 'bg-purple-50 text-purple-700 border-purple-200' },
      project: { label: 'Projeto', color: 'bg-green-50 text-green-700 border-green-200' },
      maintenance: { label: 'Manutenção', color: 'bg-orange-50 text-orange-700 border-orange-200' }
    };
    return configs[type] || { label: 'Geral', color: 'bg-gray-50 text-gray-700 border-gray-200' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, 'dd/MM/yy', { locale: ptBR }) : null;
    } catch {
      return null;
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    try {
      const date = typeof dueDate === 'string' ? parseISO(dueDate) : new Date(dueDate);
      return isValid(date) && date < new Date();
    } catch {
      return false;
    }
  };

  const handleAssign = (userId) => {
    // Impedir atribuição de responsável em tickets concluídos
    if (['resolved', 'closed'].includes(ticket.status)) {
      console.log('❌ Não é possível alterar responsável de tickets concluídos');
      return;
    }
    
    onAssign(ticket.id, userId);
  };

  const handleCardClick = (e) => {
    // Evita navegação se clicou no dropdown ou botões
    if (e.target.closest('[data-dropdown]') || e.target.closest('button')) {
      return;
    }
    navigate(`/ViewTicket?id=${ticket.id}`);
  };

  const priorityConfig = getPriorityConfig(ticket.priority);
  const typeConfig = getTypeConfig(ticket.type);
  const PriorityIcon = priorityConfig.icon;
  const dueDate = formatDate(ticket.due_date);
  const overdue = isOverdue(ticket.due_date);

  return (
    <Draggable draggableId={ticket.id} index={index}>
      {(provided, snapshot) => (
        <motion.div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          whileHover={{ y: -2, scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className={`mb-4 last:mb-0 ${snapshot.isDragging ? 'rotate-2 cursor-grabbing scale-105' : 'cursor-pointer'}`}
          onClick={handleCardClick}
        >
          <Card className={`group hover:shadow-xl transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-gray-50/50 backdrop-blur-sm ${
            snapshot.isDragging ? 'shadow-2xl ring-2 ring-primary/20' : ''
          }`}>
            <CardContent className="p-5 space-y-4">
              {/* Header com título e ações */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 line-clamp-2 text-sm leading-5 mb-1">
                    {ticket.title}
                  </h3>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span className="font-mono">#{ticket.ticket_number || ticket.id?.slice(0, 8)}</span>
                  </div>
                </div>
                <div data-dropdown>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 hover:bg-gray-100"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/ViewTicket?id=${ticket.id}`);
                      }}>
                        <Eye className="h-4 w-4 mr-2" />
                        Visualizar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/EditTicket?id=${ticket.id}`);
                      }}>
                        <Edit2 className="h-4 w-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      
                      {!(['resolved', 'closed'].includes(ticket.status)) ? (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <UserCheck className="h-4 w-4 mr-2" />
                            <span>Responsável</span>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="min-w-[220px]">
                            <DropdownMenuRadioGroup value={ticket.assigned_to || ""} onValueChange={(value) => handleAssign(value || null)}>
                              <DropdownMenuRadioItem value="">
                                <div className="flex items-center">
                                  <UserX className="h-4 w-4 mr-2 text-muted-foreground" />
                                  <span>Não atribuído</span>
                                </div>
                              </DropdownMenuRadioItem>
                              
                              <DropdownMenuSeparator />
                              
                              {availableUsers.map(user => (
                                <DropdownMenuRadioItem key={user.id} value={user.id}>
                                  <div className="flex items-center">
                                    <Avatar className="h-5 w-5 mr-2">
                                      <AvatarImage src={user.avatar_url} alt={user.name} />
                                      <AvatarFallback className="text-[10px]">
                                        {(user.name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="truncate">{user.name}</span>
                                  </div>
                                </DropdownMenuRadioItem>
                              ))}
                            </DropdownMenuRadioGroup>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      ) : (
                        <DropdownMenuItem disabled>
                          <UserCheck className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span className="text-muted-foreground">Responsável (ticket concluído)</span>
                        </DropdownMenuItem>
                      )}
                      
                      <DropdownMenuSeparator />
                      
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(ticket.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Badges de Prioridade e Tipo */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${priorityConfig.badge} flex items-center gap-1 text-xs font-medium px-2 py-1`}>
                  <PriorityIcon className={`h-3 w-3 ${priorityConfig.iconColor}`} />
                  {priorityConfig.label}
                </Badge>
                {ticket.type && (
                  <Badge variant="outline" className={`${typeConfig.color} text-xs font-medium px-2 py-1`}>
                    {typeConfig.label}
                  </Badge>
                )}
                {overdue && (
                  <Badge className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-medium px-2 py-1 animate-pulse">
                    Vencido
                  </Badge>
                )}
                {/* Tempo em aberto e alerta */}
                <Badge className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {getTimeElapsedString(ticket.created_at || ticket.created_date)}
                  {getAlertBadge(ticket.created_at || ticket.created_date, ticket.status)}
                </Badge>
              </div>

              {/* Informações do cliente/empresa e data */}
              <div className="flex items-center justify-between text-xs text-gray-600">
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  <Building2 className="h-3 w-3 shrink-0" />
                  <span className="truncate">
                    {client?.name || client?.corporate_name || 
                     company?.name || company?.corporate_name || 'Cliente'}
                  </span>
                </div>
                {dueDate && (
                  <div className={`flex items-center gap-1 shrink-0 ml-2 ${overdue ? 'text-red-600 font-medium' : ''}`}>
                    <Calendar className="h-3 w-3" />
                    <span>{dueDate}</span>
                  </div>
                )}
              </div>

              {/* Footer com responsável e comentários */}
              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                {assignedUser ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2 text-xs">
                        <Avatar className="h-6 w-6 ring-2 ring-white shadow-sm">
                          <AvatarImage src={assignedUser.avatar_url} alt={assignedUser.name} />
                          <AvatarFallback className="text-[10px] bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-medium">
                            {(assignedUser.name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate max-w-[80px] font-medium text-gray-700">
                          {assignedUser.name}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">Responsável: {assignedUser.name}</p>
                      <p className="text-xs text-muted-foreground">{assignedUser.email}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center">
                      <UserPlus className="h-3 w-3" />
                    </div>
                    <span>Não atribuído</span>
                  </div>
                )}
                
                <div className="flex items-center gap-3">
                  {(ticket.interactions?.length > 0 || ticket.comments_count > 0) && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                      <MessageSquare className="h-3 w-3" />
                      <span>{ticket.interactions?.length || ticket.comments_count || 0}</span>
                    </div>
                  )}
                  {ticket.is_favorite && (
                    <Star className="h-3 w-3 text-yellow-500 fill-current" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </Draggable>
  );
};

const Column = ({ title, color, headerColor, icon: Icon, iconColor, tickets, companies, clients, users, onUpdate, onDelete, onAssign }) => {
  return (
    <div className="flex flex-col h-full">
      {/* Header da coluna com gradiente */}
      <div className={`${headerColor} rounded-t-lg p-4 text-white shadow-lg`}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white">{title}</h3>
            <p className="text-white/80 text-sm">{tickets.length} chamados</p>
          </div>
          <Badge variant="secondary" className="bg-white/20 text-white border-white/30 font-semibold">
            {tickets.length}
          </Badge>
        </div>
      </div>

      {/* Corpo da coluna */}
      <Card className={`flex-1 rounded-t-none border-t-0 ${color} shadow-lg`}>
        <CardContent className="p-4 h-full">
          <ScrollArea className="h-[calc(100vh-16rem)]">
            <Droppable droppableId={title.toLowerCase().replace(/\s+/g, '_')}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`min-h-[200px] transition-colors duration-200 ${
                    snapshot.isDraggingOver ? 'bg-primary/5 rounded-lg' : ''
                  }`}
                >
                  <AnimatePresence>
                    {tickets.map((ticket, index) => (
                      <TicketCard
                        key={ticket.id}
                        ticket={ticket}
                        company={companies.find(c => c.id === ticket.company_id)}
                        client={clients.find(c => c.id === ticket.client_id)}
                        assignedUser={ticket.assigned_to ? users.find(u => u.id === ticket.assigned_to) : null}
                        availableUsers={users}
                        index={index}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                        onAssign={onAssign}
                      />
                    ))}
                  </AnimatePresence>
                  {provided.placeholder}
                  
                  {/* Estado vazio */}
                  {tickets.length === 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex flex-col items-center justify-center py-12 text-gray-400"
                    >
                      <Icon className="h-12 w-12 mb-3 opacity-50" />
                      <p className="text-sm font-medium">Nenhum chamado</p>
                      <p className="text-xs">Arraste chamados para cá</p>
                    </motion.div>
                  )}
                </div>
              )}
            </Droppable>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

// Função utilitária para badge de alerta inteligente
function getAlertBadge(createdAt, status) {
  if (!(['open','analysis','in_progress','reopened'].includes(status))) return null;
  const created = new Date(createdAt);
  const now = new Date();
  const diff = (now - created) / (1000 * 60 * 60); // horas
  const diffDays = diff / 24;
  if (diffDays > 30) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-black text-white animate-pulse font-bold">+30 dias</span>;
  }
  if (diffDays > 20) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-blue-900 text-white animate-pulse font-bold">+20 dias</span>;
  }
  if (diffDays > 10) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-purple-700 text-white animate-pulse font-bold">+10 dias</span>;
  }
  if (diff > 48) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-red-500 text-white animate-pulse font-bold">+48h</span>;
  }
  if (diff > 24) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-yellow-400 text-white animate-pulse font-bold">+24h</span>;
  }
  return null;
}

export default function TicketKanban({ tickets, companies, clients, onUpdate, onDelete, isLoading }) {
  const [users, setUsers] = useState([]);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingTicketId, setPendingTicketId] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    // Carregar lista de usuários
    const fetchUsers = async () => {
      try {
        console.log('🔄 Carregando usuários para Kanban...');
        const internalUsers = await UserEntity.list();
        // Filtrar apenas usuários internos (não clientes)
        const filteredUsers = internalUsers.filter(user => user.role !== 'client');
        console.log('👥 Usuários carregados:', filteredUsers.length);
        setUsers(filteredUsers);
      } catch (error) {
        console.error('❌ Erro ao carregar usuários:', error);
        setUsers([]);
      }
    };
    
    fetchUsers();
  }, []);

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const ticket = tickets.find(t => t.id === draggableId);
    if (!ticket) return;
    
    // Impedir movimento de tickets concluídos
    if (['resolved', 'closed'].includes(ticket.status)) {
      console.log('❌ Não é possível mover tickets concluídos');
      return;
    }

    // Mapear o droppableId para o status correto
    const statusMap = {
      'abertos': 'open',
      'em_análise': 'analysis', 
      'em_andamento': 'in_progress',
      'resolvidos': 'resolved',
      'cancelados': 'cancelled'
    };

    const newStatus = statusMap[destination.droppableId] || destination.droppableId;
    
    console.log('🔄 Movendo ticket:', {
      ticketId: draggableId,
      from: source.droppableId,
      to: destination.droppableId,
      newStatus
    });

    if ((newStatus === 'resolved' || newStatus === 'cancelled')) {
      setPendingStatus(newStatus);
      setPendingTicketId(draggableId);
      setShowReasonModal(true);
      return;
    }

    await onUpdate(draggableId, { ...ticket, status: newStatus });
  };

  const handleAssignTicket = async (ticketId, userId) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) return;
    
    console.log('👤 Atribuindo ticket:', { ticketId, userId });
    await onUpdate(ticketId, { ...ticket, assigned_to: userId });
  };

  // Função para confirmar motivo/solução e atualizar status
  const handleConfirmReason = async () => {
    if (!reason.trim()) return alert('Informe o motivo ou solução!');
    const ticket = tickets.find(t => t.id === pendingTicketId);
    if (!ticket) return;
    await onUpdate(pendingTicketId, { ...ticket, status: pendingStatus, reason });
    setShowReasonModal(false);
    setPendingStatus(null);
    setPendingTicketId(null);
    setReason('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="text-sm text-gray-500">Carregando chamados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-gray-50 to-white">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 h-full">
          {Object.entries(COLUMNS).map(([status, { title, color, headerColor, icon, iconColor }]) => (
            <Column
              key={status}
              title={title}
              color={color}
              headerColor={headerColor}
              icon={icon}
              iconColor={iconColor}
              tickets={tickets.filter(t => t.status === status)}
              companies={companies}
              clients={clients}
              users={users}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onAssign={handleAssignTicket}
            />
          ))}
        </div>
      </DragDropContext>
      {showReasonModal && (
        <Dialog open={showReasonModal} onOpenChange={setShowReasonModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Informe o motivo/solução</DialogTitle>
            </DialogHeader>
            <Input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Descreva o motivo ou solução..."
              autoFocus
            />
            <DialogFooter>
              <Button onClick={handleConfirmReason}>Salvar</Button>
              <DialogClose asChild>
                <Button variant="ghost" onClick={() => { setShowReasonModal(false); setReason(''); setPendingStatus(null); setPendingTicketId(null); }}>Cancelar</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}